"use client";

import * as React from "react";
import { useSearchParams } from "next/navigation";

import { useTransientScrollState } from "@/components/site/layout/use-transient-scroll-state";
import { SixPillarsInteractivePreview } from "@/components/site/sections/six-pillars-preview";
import { sixPillarsContent } from "@/lib/six-pillars-content";
import { productSectionAnchors } from "@/lib/content/navigation";
import { cn } from "@/lib/utils";

export function SixPillarsTabbedPreview() {
  const [pillarParam, setPillarParam] = React.useState<string | null>(null);

  // The surface stays outside the boundary: a surface fallback would put a
  // second copy of its headings in the HTML whenever useSearchParams suspends.
  return (
    <>
      <React.Suspense fallback={null}>
        <PillarSearchParamSync onChange={setPillarParam} />
      </React.Suspense>
      <SixPillarsTabbedPreviewSurface pillarParam={pillarParam} />
    </>
  );
}

function PillarSearchParamSync({
  onChange,
}: {
  onChange: (pillarParam: string | null) => void;
}) {
  const pillarParam = useSearchParams().get("pillar");

  React.useEffect(() => {
    onChange(pillarParam);
  }, [onChange, pillarParam]);

  return null;
}

function SixPillarsTabbedPreviewSurface({
  pillarParam,
}: {
  pillarParam?: string | null;
}) {
  const [activePillar, setActivePillar] = React.useState("overview");
  const { isScrolling, handleScroll } = useTransientScrollState();

  const activeItem =
    sixPillarsContent.find((pillar) => pillar.id === activePillar) ??
    sixPillarsContent[0];

  React.useEffect(() => {
    const applyPillar = (pillarId: string) => {
      switch (pillarId) {
        case "overview":
        case "coop-profile":
        case "fundamentals":
        case "pulse":
        case "candidates":
        case "random-coffee":
          setActivePillar(pillarId);
          return true;
        default:
          return false;
      }
    };

    const syncFromHash = () => {
      if (pillarParam && applyPillar(pillarParam)) {
        return;
      }

      const hash = window.location.hash.replace(/^#/, "");

      switch (hash) {
        case productSectionAnchors.overview:
          setActivePillar("overview");
          break;
        case productSectionAnchors["coop-profile"]:
          setActivePillar("coop-profile");
          break;
        case productSectionAnchors.fundamentals:
          setActivePillar("fundamentals");
          break;
        case productSectionAnchors.pulse:
          setActivePillar("pulse");
          break;
        case productSectionAnchors.candidates:
          setActivePillar("candidates");
          break;
        case productSectionAnchors["random-coffee"]:
          setActivePillar("random-coffee");
          break;
        default:
          break;
      }
    };

    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);

    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [pillarParam]);

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="w-full">
        <div
          data-scroll-active={isScrolling ? "true" : "false"}
          onScroll={handleScroll}
          className="-mx-4 site-scrollbar-auto overflow-x-auto px-4 sm:-mx-6 sm:px-6"
        >
          <div
            role="tablist"
            aria-label="Cooperly product pillars"
            className="flex min-w-max flex-nowrap items-center justify-start text-left"
          >
            {sixPillarsContent.map((pillar, index) => {
              const Icon = pillar.icon;
              const isActive = pillar.id === activeItem.id;
              const anchorId = productSectionAnchors[pillar.id];
              const tabId = `pillar-tab-${pillar.id}`;
              const panelId = `pillar-panel-${pillar.id}`;
              const hasSectionAnchor = anchorId === productSectionAnchors.overview;

              return (
                <div
                  key={pillar.id}
                  className={cn(
                    "relative flex items-center",
                    mobileTabOrderClass(pillar.id),
                  )}
                >
                  {hasSectionAnchor ? null : (
                    <span
                      id={anchorId}
                      aria-hidden="true"
                      className="absolute -top-24 size-px overflow-hidden"
                    />
                  )}
                  <button
                    id={tabId}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={panelId}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActivePillar(pillar.id)}
                    className={cn(
                      "site-pressable group inline-flex shrink-0 items-center gap-2.5 whitespace-nowrap px-2.5 py-2.5 text-base font-semibold tracking-[-0.02em] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] sm:gap-3 sm:px-3 sm:py-3 sm:text-lg",
                      index === 0 && "pl-0",
                      isActive
                        ? "text-foreground opacity-100"
                        : "text-muted-foreground opacity-70 hover:text-foreground hover:opacity-100",
                    )}
                  >
                    <Icon
                      className={cn(
                        "size-5 shrink-0 transition-[color,transform] duration-200 ease-[var(--ease-out)]",
                        isActive ? "text-foreground" : "text-muted-foreground",
                      )}
                    />
                    <span>{pillar.title}</span>
                  </button>
                  {index < sixPillarsContent.length - 1 ? (
                    <span
                      aria-hidden
                      className="mx-1 h-4 w-px shrink-0 self-center bg-zinc-200 sm:h-5"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        id={`pillar-panel-${activeItem.id}`}
        role="tabpanel"
        aria-labelledby={`pillar-tab-${activeItem.id}`}
        className="space-y-3 sm:space-y-4"
      >
        <div>
          <SixPillarsInteractivePreview
            key={activeItem.id}
            pillarId={activeItem.id}
          />
        </div>
        <div className="mt-3 max-w-3xl sm:mt-4">
          <p className="site-caption max-w-[44rem]">{activeItem.description}</p>
        </div>
      </div>
    </div>
  );
}

function mobileTabOrderClass(pillarId: string) {
  switch (pillarId) {
    case "overview":
      return "order-1 sm:order-none";
    case "coop-profile":
      return "order-2 sm:order-none";
    case "fundamentals":
      return "order-3 sm:order-none";
    case "pulse":
      return "order-4 sm:order-none";
    case "candidates":
      return "order-5 sm:order-none";
    case "random-coffee":
      return "order-6 sm:order-none";
    default:
      return "sm:order-none";
  }
}
